import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AthleteContext = createContext(null);

const STORAGE_KEY = 'pfap.currentAthlete';

/**
 * Persistent "current athlete" context.
 * Written to localStorage so it survives page reloads.
 * Consumed by the top-bar pill + any page that needs a default athlete.
 */
export const AthleteProvider = ({ children }) => {
  const [currentAthlete, setCurrentAthleteState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const setCurrentAthlete = useCallback((athlete) => {
    if (!athlete || !athlete.id) {
      localStorage.removeItem(STORAGE_KEY);
      setCurrentAthleteState(null);
      return;
    }
    const minimal = {
      id: athlete.id,
      full_name: athlete.full_name || athlete.name || '',
      squad: athlete.squad || '',
      position: athlete.position || '',
      setAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(minimal));
    setCurrentAthleteState(minimal);
  }, []);

  const clearCurrentAthlete = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentAthleteState(null);
  }, []);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        setCurrentAthleteState(e.newValue ? JSON.parse(e.newValue) : null);
      } catch {
        setCurrentAthleteState(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AthleteContext.Provider value={{ currentAthlete, setCurrentAthlete, clearCurrentAthlete }}>
      {children}
    </AthleteContext.Provider>
  );
};

export const useCurrentAthlete = () => {
  const ctx = useContext(AthleteContext);
  if (!ctx) {
    // Safe fallback so the app doesn't explode if a consumer is mounted
    // before the provider (shouldn't happen, but keeps lints quiet).
    return { currentAthlete: null, setCurrentAthlete: () => {}, clearCurrentAthlete: () => {} };
  }
  return ctx;
};
